/**
 * Error logging base URL (POST /log/error to hp-service).
 * Only sends to /log/error when backend exposes error_logging_enabled === true in initInfo (V3).
 * Resolution (same approach as cb-hp-ui):
 * 1. window.__CB_HP_ERROR_LOGGING_BASE_URL__ or process.env.HP_ERROR_LOGGING_BASE_URL if set (explicit override).
 * 2. Otherwise: resolve process.env.HP_SERVICE_BASE_URL template with site, app_domain, service_name from hostname.
 */

let _errorLoggingUrlLogged = false;

function isErrorLoggingEnabled() {
  if (typeof window === 'undefined') return false;
  return !!(window.initInfo && window.initInfo.error_logging_enabled === true);
}

export function getErrorLoggingBaseURL() {
  if (typeof window === 'undefined') return '';
  if (!isErrorLoggingEnabled()) return '';
  const fromWindow = window.__CB_HP_ERROR_LOGGING_BASE_URL__;
  const fromEnv = typeof process !== 'undefined' && process.env && process.env.HP_ERROR_LOGGING_BASE_URL;
  const explicitUrl = fromWindow || fromEnv || '';
  if (explicitUrl) {
    const resolved = resolveSitePlaceholder(explicitUrl);
    if (process.env.NODE_ENV !== 'production' && typeof console !== 'undefined' && console.log && !_errorLoggingUrlLogged) {
      _errorLoggingUrlLogged = true;
      console.log('[HP Error Logging] base URL:', resolved, 'source:', fromWindow ? 'window' : 'process.env');
    }
    return resolved;
  }
  const derived = getHpServiceBaseUrl();
  if (process.env.NODE_ENV !== 'production' && typeof console !== 'undefined' && console.log && !_errorLoggingUrlLogged) {
    _errorLoggingUrlLogged = true;
    console.log('[HP Error Logging] base URL:', derived || '(empty — HP_SERVICE_BASE_URL not set)', 'source:', 'template');
  }
  return derived;
}

/**
 * Derive hp-service base URL from HP_SERVICE_BASE_URL template (same approach as cb-hp-ui getMsAppBaseUrl).
 * Template placeholders: {site}, {app_domain}, {service_name} (delimited to avoid corrupting URL when values contain placeholder names).
 * Parses hostname like cb-hp-ui: defaultDomain = last two parts (e.g. localcb.in), defaultSite = rest (e.g. mannar-test).
 */
export function getHpServiceBaseUrl() {
  if (typeof window === 'undefined' || !window.location || !window.location.hostname) return '';
  const template = typeof process !== 'undefined' && process.env && process.env.HP_SERVICE_BASE_URL;
  if (!template || typeof template !== 'string') return '';
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  if (parts.length < 2) return '';
  const defaultDomain = [parts.pop(), parts.pop()].reverse().join('.');
  const defaultSite = parts.length ? parts.join('.') : '';
  if (defaultSite === '' && template.indexOf('{site}') !== -1) {
    return '';
  }
  const regionPrefix = (typeof window !== 'undefined' && window.cb_hp_domain_prefix) || '';
  const appDomain = regionPrefix ? regionPrefix + '.' + defaultDomain : defaultDomain;
  const serviceName = defaultDomain.indexOf('predev') !== -1 ? 'hpv4' : 'hp';
  return template
    .replace(/\{app_domain\}/g, appDomain)
    .replace(/\{site\}/g, defaultSite)
    .replace(/\{service_name\}/g, serviceName);
}

/** Derive current site from window.location.hostname (first segment for localcb; subdomain otherwise). */
export function getDefaultSiteForErrorLogging() {
  if (typeof window === 'undefined' || !window.location || !window.location.hostname) return '';
  const hostname = window.location.hostname;
  if (hostname.indexOf('localcb') !== -1) {
    return hostname.split('.')[0] || '';
  }
  const parts = hostname.split('.');
  if (parts.length < 3) return parts[0] || '';
  parts.pop();
  parts.pop();
  return parts.join('.');
}

/** Replace "site." placeholder in url with current site (for explicit override URLs). */
function resolveSitePlaceholder(url) {
  if (!url || typeof url !== 'string') return url;
  if (url.indexOf('site.') === -1) return url;
  const site = getDefaultSiteForErrorLogging();
  if (site === '' && url.indexOf('site.') !== -1) {
    if (process.env.NODE_ENV !== 'production' && typeof console !== 'undefined' && console.warn) {
      console.warn('[Error Logger] Site empty, explicit URL has site. placeholder; skipping.');
    }
    return '';
  }
  return url.replace('site.', site + '.');
}

// Dev only: expose getter so you can confirm the resolved URL in console without triggering an error.
// In browser console run: __CB_GET_ERROR_LOGGING_BASE_URL__()
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  window.__CB_GET_ERROR_LOGGING_BASE_URL__ = getErrorLoggingBaseURL;
}
